import { Link, Navigate } from 'react-router-dom';
import { t, tx } from '../../i18n';
import { fmtDayMonth, monthName, monthShort } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { useClientApp } from '../context';
import { badgeClass, belegCount, belegePath, FOLDERS, monthBadge, noteText } from './labels';
import { useBelegePage } from './nav';
import type { FirmsResponse, MonthResponse, MonthsResponse, YearsResponse } from './types';

// Ebenen der Belege bis zum Monat: Firma → Jahr → Monat → Ordner. Der Ordner selbst ist FolderPage.

export function Loading({ error }: { error?: string | null }) {
  return error ? (
    <p className="muted" role="alert">
      {error}
    </p>
  ) : (
    <div className="spinner" aria-label={t('Lädt')} />
  );
}

// Buchhaltung · Für welche Firma? (nur mit mehreren Firmen)
export function FirmsPage() {
  const { clients } = useClientApp();
  const one = clients.length === 1 ? clients[0]!.id : null;
  const res = useApi<FirmsResponse>(one ? null : '/api/app/belege', { live: ['belege'] });
  useBelegePage({ title: t('Finanzbuchhaltung') });
  if (one) return <Navigate to={belegePath(one)} replace />;
  return (
    <>
      <div className="hello">
        <h2>{t('Buchhaltung')}</h2>
        <p>{t('Für welche Firma?')}</p>
      </div>
      {!res.data ? (
        <Loading error={res.error} />
      ) : (
        <div className="list">
          {res.data.firms.map((f) => (
            <Link key={f.clientId} className="item" to={belegePath(f.clientId)}>
              <span className="ico" aria-hidden="true">
                {f.name.charAt(0)}
              </span>
              <span className="tx">
                <b>{f.name}</b>
                <small>
                  {f.year}: {belegCount(f.count)}
                </small>
              </span>
              <span className={badgeClass(f.badge.tone)}>{tx(f.badge.label)}</span>
              <span className="chev" aria-hidden="true">
                ›
              </span>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

// <Firma> · Welches Jahr?
export function YearsPage({ clientId }: { clientId: string }) {
  const { clientName } = useClientApp();
  const res = useApi<YearsResponse>(`/api/app/belege/${clientId}/years`, { live: ['belege'], clientId });
  const name = res.data?.client.name ?? clientName(clientId);
  useBelegePage({ title: name, clientId, clientName: name });
  return (
    <>
      <div className="hello">
        <h2>{name}</h2>
        <p>{t('Welches Jahr?')}</p>
      </div>
      {!res.data ? (
        <Loading error={res.error} />
      ) : (
        <div className="list">
          {res.data.years.map((y) => (
            <Link key={y.year} className="item" to={belegePath(clientId, y.year)}>
              <span className="ico" aria-hidden="true">
                {String(y.year).slice(2)}
              </span>
              <span className="tx">
                <b>{y.year}</b>
                <small>{belegCount(y.count)}</small>
              </span>
              <span className={badgeClass(y.badge.tone)}>{tx(y.badge.label)}</span>
              <span className="chev" aria-hidden="true">
                ›
              </span>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

// <Jahr> · Welcher Monat?
export function MonthsPage({ clientId, year }: { clientId: string; year: number }) {
  const res = useApi<MonthsResponse>(`/api/app/belege/${clientId}/years/${year}`, { live: ['belege'], clientId });
  useBelegePage({ title: String(year), clientId, clientName: res.data?.client.name, year });
  return (
    <>
      <div className="hello">
        <h2>{year}</h2>
        <p>{t('Welcher Monat?')}</p>
      </div>
      {!res.data ? (
        <Loading error={res.error} />
      ) : (
        <div className="months">
          {res.data.months.map((m) => {
            const b = monthBadge(m.state, m.count);
            const content = (
              <>
                <b>{monthShort(m.month)}</b>
                <span className={`badge ${b.cls}`}>{b.label}</span>
              </>
            );
            return m.state === 'zukunft' ? (
              <div
                key={m.month}
                className="month off"
                aria-disabled="true"
                aria-label={t('{month} {year}: noch nicht', { month: monthName(m.month), year })}
              >
                {content}
              </div>
            ) : (
              <Link
                key={m.month}
                className="month"
                to={belegePath(clientId, year, m.month)}
                aria-label={`${monthName(m.month)} ${year}: ${b.label}`}
              >
                {content}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}

// <Monat> <Jahr> · Was laden Sie hoch?
export function FoldersPage({ clientId, year, month }: { clientId: string; year: number; month: number }) {
  const res = useApi<MonthResponse>(`/api/app/belege/${clientId}/years/${year}/${month}`, { live: ['belege'], clientId });
  const d = res.data;
  useBelegePage({ title: monthName(month), clientId, clientName: d?.client.name, year, month });
  if (d?.future) return <Navigate to={belegePath(clientId, year)} replace />;
  const st = d?.status;
  return (
    <>
      <div className="hello">
        <h2>
          {monthName(month)} {year}
        </h2>
        <p>{t('Was laden Sie hoch?')}</p>
      </div>
      {!d ? (
        <Loading error={res.error} />
      ) : (
        <>
          {st?.status === 'fehlt' && (
            <div className="card mstat warn">
              <b>{t('Ihre Kanzlei: Beleg fehlt')}</b>
              <div>
                {noteText(st.note)
                  ? t('Es fehlt noch: {note}. Bitte hier hochladen.', { note: noteText(st.note) })
                  : t('Es fehlt noch: Belege. Bitte hier hochladen.')}
              </div>
            </div>
          )}
          {st?.status === 'gebucht' && (
            <div className="card mstat">
              <span className="badge b-ok">{t('gebucht ✓')}</span>{' '}
              {d.belege.every((b) => b.booked)
                ? t('Ihre Kanzlei hat diesen Monat gebucht.')
                : t('Ihre Kanzlei hat diesen Monat gebucht. Neue Belege prüft sie noch.')}
            </div>
          )}
          {st?.status === 'nachgereicht' && (
            <div className="card mstat">
              <span className="badge b-none">{t('nachgereicht')}</span> {nachgereichtText(st.nachgereichtOn, st.note)}
            </div>
          )}
          <div className="list">
            {FOLDERS.map((f) => {
              const inFolder = d.belege.filter((b) => b.category === f.key);
              const n = inFolder.length;
              // geschriebene Rechnungen sind nicht „hochgeladen“ (wie beim Monat)
              const re = inFolder.filter((b) => b.rechnung).length;
              return (
                <Link key={f.key} className="item" to={belegePath(clientId, year, month, f.key)}>
                  <span className="ico" aria-hidden="true">
                    {f.ico}
                  </span>
                  <span className="tx">
                    <b>{t(f.label)}</b>
                    <small>{t(f.sub)}</small>
                  </span>
                  {!n ? (
                    <span className="badge b-none">{t('leer')}</span>
                  ) : st?.status === 'gebucht' && inFolder.every((b) => b.booked) ? (
                    <span className="badge b-ok">{t('{n} gebucht ✓', { n })}</span>
                  ) : n > re ? (
                    <span className="badge b-none">{t('{n} hochgeladen', { n: n - re })}</span>
                  ) : (
                    <span className="badge b-none">{re === 1 ? t('1 Rechnung') : t('{n} Rechnungen', { n: re })}</span>
                  )}
                  <span className="chev" aria-hidden="true">
                    ›
                  </span>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

// „Am 07.10. nachgereicht: Kontoauszug. Ihre Kanzlei prüft.“ (ohne Datum „Nachgereicht“, ohne Hinweis ohne Doppelpunkt)
function nachgereichtText(on: string | null, rawNote: string | null): string {
  const note = noteText(rawNote);
  if (on) {
    const date = fmtDayMonth(on);
    return rawNote
      ? t('Am {date} nachgereicht: {note}. Ihre Kanzlei prüft.', { date, note })
      : t('Am {date} nachgereicht. Ihre Kanzlei prüft.', { date });
  }
  return rawNote ? t('Nachgereicht: {note}. Ihre Kanzlei prüft.', { note }) : t('Nachgereicht. Ihre Kanzlei prüft.');
}
